import {forwardRef, useCallback, Ref, MouseEvent} from 'react';

import {Link, useNavigate} from 'react-router-dom';
import styled, {css} from 'styled-components';

import {generateButtonCSS} from 'components/design-guide/Action/Button/styles';
import {generateTextLinkStylesCSS} from 'components/design-guide/Action/Hyperlink/styles';
import {
  AnalyticsAnchorProps,
  AnalyticsButtonProps,
  AnalyticsLinkProps,
  ActionProps
} from 'components/design-guide/Action/types';
import {ActionStyle, ActionType, ButtonWidth} from 'components/design-guide/enums';
import {withAnalyticsID} from 'shared/Ensighten/components/withAnalyticsID';
import {Breakpoints, Spacing} from 'styles/Base';
import {trackClick} from 'utils/adobe-target/abtest-prefetch';
import {generateMargins, generatePadding} from 'utils/components';

export const TopLevelSpan = styled.span<ActionProps>`
  display: ${({actionStyle}) => (actionStyle === ActionStyle.INLINE_TEXT ? 'inline-block' : 'block')};
  ${({margin}) => (margin ? generateMargins(margin) : '')}
  ${({padding}) => (padding ? generatePadding(padding) : '')}
  ${(props) => {
    if (props.actionStyle !== ActionStyle.BUTTON) {
      return css``;
    }
    if (props.styleProps.width === ButtonWidth.FULL) {
      return css`
        align-self: stretch;
      `;
    }
    if (props.styleProps.width === ButtonWidth.FULL_AT_MOBILE) {
      return css`
        ${`@media (${Breakpoints.MOBILE})`} {
          align-self: stretch;
        }
      `;
    }
    return css``;
  }}
`;

const StyledRouterLink = styled(Link)<{$actionProps: ActionProps}>`
  text-decoration: none;
  ${({$actionProps}) => css`
    ${generateButtonCSS($actionProps)}
    ${generateTextLinkStylesCSS($actionProps)}
  `}
`;

export const ActionTypeRouterComponent = withAnalyticsID<AnalyticsLinkProps>(
  forwardRef(({children, ...props}: AnalyticsLinkProps, ref: Ref<HTMLAnchorElement>) => {
    const {actionType, typeProps, disabled, actionStyle, styleProps, reportToTargetOnClick} = props.$actionProps;
    const isDisabled = disabled || (actionStyle === ActionStyle.BUTTON && styleProps.withSpinner?.active);
    const navigate = useNavigate();

    const onRouterLinkClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isDisabled) {
          event.preventDefault();
        } else if (actionType === ActionType.ROUTER && typeProps.beforeNavigation) {
          trackClick(reportToTargetOnClick);
          event.preventDefault();
          typeProps.beforeNavigation(event);
          navigate(typeProps.to, {state: typeProps.state});
        }
      },
      [isDisabled, actionType, typeProps, reportToTargetOnClick, navigate]
    );

    return (
      <StyledRouterLink
        {...props}
        $actionProps={{...props.$actionProps, disabled: isDisabled}}
        state={actionType === ActionType.ROUTER ? typeProps.state : undefined}
        ref={ref}
        onClick={onRouterLinkClick}>
        {children}
      </StyledRouterLink>
    );
  })
);

const StyledExternalLink = styled.a<ActionProps>`
  text-decoration: none;
  cursor: pointer;
  ${(props) => css`
    ${generateButtonCSS(props)}
    ${generateTextLinkStylesCSS(props)}
  `}
`;

export const ActionTypeExternalComponent = withAnalyticsID<AnalyticsAnchorProps>(
  forwardRef(({children, ...props}: AnalyticsAnchorProps, ref: Ref<HTMLAnchorElement>) => {
    const {actionType, typeProps, disabled, actionStyle, styleProps} = props;
    const isDisabled = disabled || (actionStyle === ActionStyle.BUTTON && styleProps.withSpinner?.active);

    const onExternalLinkClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isDisabled) {
          event.preventDefault();
          return;
        }
        trackClick(props.reportToTargetOnClick);
        if (actionType === ActionType.EXTERNAL && typeProps.href && typeProps.beforeNavigation) {
          event.preventDefault();
          typeProps.beforeNavigation(event);
          window.location.assign(typeProps.href);
        } else if ((actionType === ActionType.PHONE || actionType === ActionType.EMAIL) && typeProps.beforeNavigation) {
          typeProps.beforeNavigation(event);
        }
      },
      [isDisabled, props.reportToTargetOnClick, actionType, typeProps]
    );

    return (
      <StyledExternalLink {...props} ref={ref} onClick={onExternalLinkClick} disabled={isDisabled}>
        {children}
      </StyledExternalLink>
    );
  })
);

const StyledButton = styled.button<ActionProps>`
  margin: ${Spacing.ZERO};
  padding: ${Spacing.ZERO};
  border: ${Spacing.ZERO};
  background: transparent;
  ${(props) => css`
    ${generateButtonCSS(props)}
    ${generateTextLinkStylesCSS(props)}
  `}
`;

export const ActionTypeOnClickComponent = withAnalyticsID<AnalyticsButtonProps>(
  forwardRef(({children, ...props}: AnalyticsButtonProps, ref: Ref<any>) => {
    const {actionType, typeProps, disabled, actionStyle, styleProps} = props;
    const isDisabled = disabled || (actionStyle === ActionStyle.BUTTON && styleProps.withSpinner?.active);

    if (actionType !== ActionType.ONCLICK) {
      return null;
    }

    return (
      <StyledButton
        {...props}
        ref={ref}
        id={props.name}
        disabled={isDisabled}
        type={typeProps.type}
        onClick={(e) => {
          trackClick(props.reportToTargetOnClick);
          typeProps?.onClick && typeProps.onClick(e);
        }}>
        {children}
      </StyledButton>
    );
  })
);
